/**
 * useOpportunityDecision.js
 *
 * Suspende a criação da cotação até o vendedor decidir a oportunidade.
 *
 * O PROBLEMA QUE ESTE HOOK RESOLVE
 * ────────────────────────────────
 * Três telas criam cotação (`AereoPage`, `NewQuotation`, `useAssistantFlow`)
 * e todas precisam do mesmo comportamento: se o backend responder 409
 * `decisao_de_oportunidade`, abrir o modal, esperar a resposta e reenviar
 * a MESMA criação com a decisão.
 *
 * Escrever esse laço três vezes é o desenho que fez o fix do bloqueio
 * regredir em 11/06: regra num caminho, buraco no vizinho. Aqui ele existe
 * uma vez, e cada tela troca `await api.criar(x)` por
 * `await criarComDecisao(extra => api.criar({ ...x, ...extra }))`.
 *
 * POR QUE PROMISE E NÃO CALLBACK
 * ──────────────────────────────
 * A criação de cotação aérea faz mais coisas DEPOIS do pai: cria as
 * alternativas e navega. Suspender a própria linha do `await` mantém o
 * resto da função intacto — nada precisa ser reorganizado em callbacks.
 *
 * CANCELAR NÃO É ERRO
 * ───────────────────
 * Se o vendedor fecha o modal, devolvemos `{ __cancelled: true }` em vez
 * de rejeitar. Cancelar é decisão legítima, não falha — rejeitar encheria
 * o console de erro e dispararia os `catch` de falha real das telas.
 */
import { useCallback, useRef, useState } from 'react';

export function useOpportunityDecision() {
  const [decisao, setDecisao] = useState(null);   // corpo do 409
  const [busy, setBusy] = useState(false);
  const pendente = useRef(null);                  // { criar, resolve }

  /**
   * @param criar  (extra) => Promise<resposta>  — extra entra no payload
   * @returns resposta da criação, ou { __cancelled: true }
   */
  const criarComDecisao = useCallback(async (criar) => {
    const resposta = await criar({});

    const precisaDecidir =
      resposta?.__status === 409 &&
      resposta?.error === 'decisao_de_oportunidade';

    if (!precisaDecidir) return resposta;

    return new Promise((resolve) => {
      pendente.current = { criar, resolve };
      setDecisao(resposta);
    });
  }, []);

  const decidir = useCallback(async ({ decision, opportunityId }) => {
    const alvo = pendente.current;
    if (!alvo) return;

    setBusy(true);
    try {
      const resposta = await alvo.criar({
        opportunity_decision: decision,
        ...(opportunityId ? { opportunity_id: opportunityId } : {}),
        // A MESMA chave do 409 volta ao servidor. É ela que faz duplo
        // clique e reenvio de rede criarem UMA oportunidade, não duas.
        idempotency_key: decisao?.idempotency_key,
      });
      pendente.current = null;
      setDecisao(null);
      alvo.resolve(resposta);
    } finally {
      setBusy(false);
    }
  }, [decisao]);

  const cancelar = useCallback(() => {
    const alvo = pendente.current;
    pendente.current = null;
    setDecisao(null);
    if (alvo) alvo.resolve({ __cancelled: true });
  }, []);

  return { decisao, busy, criarComDecisao, decidir, cancelar };
}
